import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Send, Check } from "lucide-react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/auth";

export default function ReferralLanding() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [submitErr, setSubmitErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        await api.post(`/referrals/track/${code}`);
        const res = await api.get(`/referrals/resolve/${code}`);
        setData(res.data);
      } catch {
        setErr("Invalid referral link");
      }
    })();
  }, [code]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitErr("");
    try {
      await api.post(`/referrals/${code}/lead`, form);
      setSent(true);
    } catch (e2) {
      setSubmitErr(formatApiError(e2));
    } finally {
      setSubmitting(false);
    }
  };

  if (err) return <div className="min-h-screen flex items-center justify-center">{err}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;

  const color = data.agency?.branding?.primary_color || "#0F172A";
  const logo = data.agency?.branding?.logo_url;

  return (
    <div className="min-h-screen hero-gradient flex items-center justify-center p-6">
      <div className="bento p-12 max-w-lg w-full text-center" data-testid="referral-landing">
        {logo ? (
          <img src={logo} alt={data.agency.name} className="h-12 object-contain mx-auto mb-5" />
        ) : (
          <img src="/logo-mark.png" alt="Foliosy" className="w-12 h-12 object-contain mx-auto mb-4 opacity-60" />
        )}
        <p className="eyebrow mb-3">You've been referred</p>
        <h1 className="text-4xl font-bold tracking-tight mb-3">Welcome to {data.agency.name}</h1>

        {sent ? (
          <div className="mt-6" data-testid="referral-lead-sent">
            <div
              className="w-12 h-12 rounded-full text-white flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: color }}
            >
              <Check className="w-6 h-6" />
            </div>
            <h2 className="text-xl font-bold mb-2">Thanks, {form.name.split(" ")[0]}!</h2>
            <p className="text-slate-600 text-sm">
              Your message has been sent to {data.agency.name}. They'll be in touch shortly.
            </p>
          </div>
        ) : !showForm ? (
          <>
            <p className="text-slate-600 mb-8">
              Get in touch with {data.agency.name} to start working together.
            </p>
            <button
              onClick={() => setShowForm(true)}
              className="inline-block text-white rounded-full px-7 py-3.5 font-medium hover:opacity-90 active:scale-95"
              style={{ backgroundColor: color }}
              data-testid="referral-cta"
            >
              Get in touch
            </button>
            <p className="mt-6 text-xs text-slate-400 font-mono">ref: {code}</p>
          </>
        ) : (
          <form onSubmit={submit} className="text-left space-y-3 mt-6" data-testid="referral-lead-form">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Your name</label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-slate-900/5 focus:border-slate-900 outline-none"
                data-testid="referral-lead-name"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Email</label>
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-slate-900/5 focus:border-slate-900 outline-none"
                data-testid="referral-lead-email"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Tell them what you need</label>
              <textarea
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Briefly describe your project…"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-slate-900/5 focus:border-slate-900 outline-none resize-none"
                data-testid="referral-lead-message"
              />
            </div>
            {submitErr && (
              <p className="text-sm text-red-500" data-testid="referral-lead-error">{submitErr}</p>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="w-full text-white rounded-full py-3 font-medium hover:opacity-90 active:scale-95 disabled:opacity-50 inline-flex items-center justify-center gap-2"
              style={{ backgroundColor: color }}
              data-testid="referral-lead-submit"
            >
              <Send className="w-4 h-4" />
              {submitting ? "Sending…" : `Send to ${data.agency.name}`}
            </button>
            <p className="text-xs text-slate-400 font-mono text-center pt-2">ref: {code}</p>
          </form>
        )}
      </div>
    </div>
  );
}
